/**
 * MintLocked.jsx
 *
 * Shown instead of the mint UI while `mint_page_unlock_at` (Supabase
 * `mint_config`) is still in the future. Big countdown, nothing else.
 *
 * When it hits zero the page reveals itself without a reload — the
 * parent re-evaluates the gate on every tick.
 */
import React, { useEffect, useState } from 'react';
import Panel from '../Panel';
import GlowText from '../GlowText';

function split(sec) {
  const s = Math.max(0, sec);
  return {
    d: Math.floor(s / 86400),
    h: Math.floor((s % 86400) / 3600),
    m: Math.floor((s % 3600) / 60),
    s: Math.floor(s % 60),
  };
}

function Unit({ value, label }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="mech-panel flex items-center justify-center"
        style={{ minWidth: 'clamp(58px, 17vw, 96px)', padding: 'clamp(10px,2.5vw,18px) 0' }}
      >
        <GlowText
          text={String(value).padStart(2, '0')}
          variant="cyan"
          mono
          style={{ fontSize: 'clamp(26px, 7vw, 46px)', lineHeight: 1 }}
        />
      </div>
      <span className="font-mono-hud text-dim" style={{ fontSize: 11, letterSpacing: '0.14em' }}>
        {label}
      </span>
    </div>
  );
}

export default function MintLocked({ unlockAt, announcement }) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = unlockAt - now;
  const t = split(remaining);
  const unlockDate = new Date(unlockAt * 1000);

  return (
    <Panel title="MINT TERMINAL // LOCKED">
      <div className="flex flex-col items-center text-center gap-7 py-6">

        <div
          className="w-24 h-24 flex items-center justify-center float-anim"
          style={{
            border: '2px solid var(--cyan)',
            clipPath: 'polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)',
            background: 'radial-gradient(circle at 40% 30%, rgba(0,229,255,0.18), rgba(10,15,28,0.9))',
            boxShadow: '0 0 40px rgba(0,229,255,0.35)',
          }}
        >
          <GlowText text="⧗" size={38} variant="cyan" />
        </div>

        <div className="space-y-2">
          <GlowText
            as="div"
            text="MINT OPENS IN"
            variant="cyan"
            style={{ fontSize: 'clamp(15px, 3.2vw, 22px)' }}
          />
          <p className="font-mono-hud text-dim text-fluid-sm">
            {unlockDate.toUTCString().replace('GMT', 'UTC')}
          </p>
        </div>

        <div className="flex items-start justify-center gap-2 sm:gap-3 flex-wrap">
          {t.d > 0 && <Unit value={t.d} label="DAYS" />}
          <Unit value={t.h} label="HOURS" />
          <Unit value={t.m} label="MINS" />
          <Unit value={t.s} label="SECS" />
        </div>

        {announcement && (
          <div className="hud-chip max-w-lg">
            <span className="status-dot" />
            <span className="font-mono-hud text-fluid-sm" style={{ color: 'var(--text)' }}>
              {announcement}
            </span>
          </div>
        )}

        <p className="font-mono-hud text-dim text-fluid-sm max-w-md">
          The mint terminal unlocks automatically when the countdown ends —
          no need to refresh. Complete the whitelist tasks in the meantime.
        </p>
      </div>
    </Panel>
  );
}
